import { useCallback, useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Trash2, Edit, Eye, EyeOff, Plus, X } from 'lucide-react';
import AICourseGenerator from '@/components/AICourseGenerator';
import AIPlanGenerator from '@/components/AIPlanGenerator';

const TYPES = [
  { key: 'lessons', label: 'Lessons' },
  { key: 'plans', label: 'Growth Plans' },
  { key: 'therapists', label: 'Therapists' },
  { key: 'events', label: 'Workshops' },
  { key: 'resources', label: 'Resources' },
  { key: 'podcast', label: 'Podcast' },
  { key: 'supporting_men', label: 'Support Guides' },
];

export default function ContentManager() {
  const [type, setType] = useState('lessons');
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item or {} for new
  const [json, setJson] = useState('');

  const load = useCallback(() => http.get(`/admin/content/${type}`).then(r => setItems(r.data)), [type]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({}); setJson('{\n  "title": "",\n  "published": true\n}'); };
  const openEdit = (item) => { setEditing(item); setJson(JSON.stringify(item, null, 2)); };
  const save = async () => {
    try { const parsed = JSON.parse(json);
      await http.post(`/admin/content/${type}`, parsed);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error('Invalid JSON or save failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete permanently?')) return;
    await http.delete(`/admin/content/${type}/${id}`); toast.success('Deleted'); load();
  };
  const togglePub = async (id) => { await http.post(`/admin/content/${type}/${id}/publish`); load(); };

  return (
    <div className="bg-white rounded-3xl p-6 card-soft" data-testid="cms-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="font-heading text-lg">Content Manager</p>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <AICourseGenerator onPublished={() => { setType('lessons'); load(); }} />
          <AIPlanGenerator onPublished={() => { setType('plans'); load(); }} />
          <button data-testid="cms-new" onClick={openNew} className="text-xs bg-navy text-white rounded-full px-4 py-2 inline-flex items-center gap-1"><Plus size={12}/>New</button>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-3">
        {TYPES.map(t => (
          <button key={t.key} data-testid={`cms-tab-${t.key}`} onClick={()=>setType(t.key)}
            className={`whitespace-nowrap text-xs px-3 py-1.5 rounded-full ${type===t.key ? 'bg-navy text-white' : 'bg-beige text-navy'}`}>
            {t.label} ({items.length && type===t.key ? items.length : ''})
          </button>
        ))}
      </div>
      <div className="divide-y divide-navy/5 mt-2">
        {items.map((it, idx) => (
          <div key={`${type}-${it.id}-${idx}`} className="py-3 flex items-center justify-between gap-3" data-testid={`cms-item-${it.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{it.title || it.name || it.id}</p>
              <p className="text-[10px] text-navy/50">{it.id}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={()=>togglePub(it.id)} title="Toggle publish" className="w-8 h-8 rounded-full hover:bg-beige flex items-center justify-center">
                {it.published !== false ? <Eye size={14} className="text-green"/> : <EyeOff size={14} className="text-navy/40"/>}
              </button>
              <button onClick={()=>openEdit(it)} className="w-8 h-8 rounded-full hover:bg-beige flex items-center justify-center"><Edit size={14}/></button>
              <button onClick={()=>del(it.id)} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-red-600"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={()=>setEditing(null)}>
          <div onClick={e=>e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-3xl p-6" data-testid="cms-editor">
            <div className="flex items-center justify-between mb-4">
              <p className="font-heading text-xl">{editing.id ? 'Edit' : 'New'} {type}</p>
              <button onClick={()=>setEditing(null)}><X size={18}/></button>
            </div>
            <textarea data-testid="cms-json" value={json} onChange={e=>setJson(e.target.value)} rows={18}
              className="w-full font-mono text-xs bg-[#F9F8F6] border border-navy/10 rounded-2xl p-4 focus:outline-none focus:ring-2 focus:ring-gold" />
            <p className="text-[10px] text-navy/50 mt-2">Edit fields as JSON. Set &quot;published&quot;: false to hide from users.</p>
            <button data-testid="cms-save" onClick={save} className="mt-3 w-full bg-navy text-white rounded-full py-3 font-medium hover:bg-navy-soft">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
